// SubstituteAttendance.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";

const SubstituteAttendance = ({ onClose }) => {
  const router = useLocation();
  const [assignedLeaves, setAssignedLeaves] = useState([]);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [loading, setLoading] = useState(false);
  const [facultyData, setFacultyData] = useState(null);
  const [absentFacultyData, setAbsentFacultyData] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [attendanceMode, setAttendanceMode] = useState("self");
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [markedAttendance, setMarkedAttendance] = useState({});
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [absenteesInput, setAbsenteesInput] = useState("");
  const [presenteesInput, setPresenteesInput] = useState("");
  const [timetableEntry, setTimetableEntry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [totalClasses, setTotalClasses] = useState("");
  const [canAddAttendance, setCanAddAttendance] = useState(false);

  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    if (router.state?.loginid) {
      console.log("Current Faculty ID:", router.state.loginid);
      fetchAssignedLeaves();
      fetchCurrentFaculty();
      fetchStudents();
    } else {
      toast.error("Faculty ID not found. Please login again.");
    }
  }, []);

  const fetchAssignedLeaves = async () => {
    if (!router.state?.loginid) return;

    setLoading(true);
    try {
      console.log("Fetching leaves for substitute:", router.state.loginid);
      const response = await axios.get(
        `${baseApiURL()}/faculty/leave/getBySubstitute/${router.state.loginid}`
      );
      console.log("Leaves response:", response.data);
      
      if (response.data.success) {
        // Show approved leaves only
        const activeLeaves = response.data.leaves.filter(
          leave => leave.status === "approved"
        );
        setAssignedLeaves(activeLeaves);
        console.log("Active leaves found:", activeLeaves);
      }
    } catch (error) {
      console.error("Error fetching assigned leaves:", error);
      toast.error("Failed to fetch assigned leaves");
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentFaculty = async () => {
    if (!router.state?.loginid) return;

    try {
      const response = await axios.post(
        `${baseApiURL()}/faculty/details/getDetails`,
        { employeeId: router.state.loginid }
      );

      if (response.data.success) {
        setFacultyData(response.data.user[0]);
        console.log("Current faculty data:", response.data.user[0]);
      }
    } catch (error) {
      console.error("Error fetching faculty data:", error);
    }
  };

  const fetchAbsentFacultyData = async (employeeId) => {
    try {
      console.log("Fetching absent faculty data for:", employeeId);
      const response = await axios.post(
        `${baseApiURL()}/faculty/details/getDetails`,
        { employeeId }
      );

      if (response.data.success) {
        setAbsentFacultyData(response.data.user[0]);
        console.log("Absent faculty timetable:", response.data.user[0].timetable);
      }
    } catch (error) {
      console.error("Error fetching absent faculty data:", error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(`${baseApiURL()}/student/details/getDetails2`);
      if (response.data.success) {
        setStudents(response.data.students);
        console.log("Students loaded:", response.data.students.length);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast.error("Failed to load students");
    }
  };

  const handleLeaveSelect = async (leave) => {
    console.log("Selected leave:", leave);
    setSelectedLeave(leave);
    setSelectedDate("");
    setSelectedPeriod("");
    setSelectedDay("");
    setTimetableEntry(null);
    setMarkedAttendance({});
    setFilteredStudents([]);
    
    await fetchAbsentFacultyData(leave.facultyId);
  };

  const handleDateSelect = (date) => {
    console.log("Selected date:", date);
    setSelectedDate(date);
    setSelectedPeriod("");
    setSelectedDay("");
    setTimetableEntry(null);
    setMarkedAttendance({});
    setFilteredStudents([]);
    
    // Get day from date
    const dateObj = new Date(date);
    const dayIndex = dateObj.getDay(); // 0 = Sunday, 1 = Monday, etc.
    if (dayIndex >= 1 && dayIndex <= 6) {
      const dayName = daysOfWeek[dayIndex - 1];
      setSelectedDay(dayName);
      console.log("Day from date:", dayName);
    }
  };

  const handlePeriodChange = (e) => {
    const period = e.target.value;
    setSelectedPeriod(period);
    console.log("Selected period:", period);
    
    if (period && selectedDay) {
      let timetableData = null;
      
      if (attendanceMode === "substitute" && absentFacultyData) {
        console.log("Looking in absent faculty timetable:", absentFacultyData.timetable);
        // Get timetable entry for absent faculty
        const dayEntry = absentFacultyData.timetable?.find(entry => entry.day === selectedDay);
        if (dayEntry) {
          console.log("Day entry found:", dayEntry);
          timetableData = dayEntry.periods.find(p => p.periodNumber === Number(period));
          console.log("Period entry found:", timetableData);
        }
      } else if (attendanceMode === "self" && facultyData) {
        console.log("Looking in current faculty timetable:", facultyData.timetable);
        // Get timetable entry for current faculty
        const dayEntry = facultyData.timetable?.find(entry => entry.day === selectedDay);
        if (dayEntry) {
          console.log("Day entry found:", dayEntry);
          timetableData = dayEntry.periods.find(p => p.periodNumber === Number(period));
          console.log("Period entry found:", timetableData);
        }
      }
      
      if (timetableData) {
        setTimetableEntry(timetableData);
        filterStudentsForClass(timetableData);
        setCanAddAttendance(true);
      } else {
        toast.error(`No class scheduled for ${selectedDay} Period ${period}`);
        setTimetableEntry(null);
        setFilteredStudents([]);
        setCanAddAttendance(false);
      }
    }
  };

  const filterStudentsForClass = (classInfo) => {
    console.log("Filtering students for class:", classInfo);
    let filtered = students;

    if (classInfo.branch) {
      filtered = filtered.filter(
        (student) => student.branch.toLowerCase() === classInfo.branch.toLowerCase()
      );
    }

    if (classInfo.semester) {
      filtered = filtered.filter(
        (student) => String(student.semester) === String(classInfo.semester)
      );
    }

    if (classInfo.section) {
      filtered = filtered.filter((student) => student.section === classInfo.section);
    }

    filtered.sort(sortEnrollmentNo);

    console.log("Filtered students:", filtered.length);
    setFilteredStudents(filtered);
  };

  // Toggle individual attendance
  const toggleAttendance = (student) => {
    setMarkedAttendance((prev) => {
      const newState = { ...prev };
      if (newState[student.enrollmentNo]) {
        delete newState[student.enrollmentNo];
      } else {
        newState[student.enrollmentNo] = {
          enrollmentNo: student.enrollmentNo,
          name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim(),
          branch: student.branch,
          section: student.section,
          subject: timetableEntry?.subject,
          period: selectedPeriod,
          semester: timetableEntry?.semester,
          date: selectedDate,
          markedBy: router.state?.loginid,
          markedByName: facultyData ? `${facultyData.firstName} ${facultyData.lastName}` : "",
          isSubstitute: attendanceMode === "substitute",
          originalFaculty: attendanceMode === "substitute" ? selectedLeave?.facultyId : null,
          originalFacultyName: attendanceMode === "substitute" ? selectedLeave?.facultyName : null
        };
      }
      return newState;
    });
  };

  // Toggle select all
  const toggleSelectAll = () => {
    const newSelectAllChecked = !selectAllChecked;
    setSelectAllChecked(newSelectAllChecked);
    
    if (newSelectAllChecked) {
      const attendanceData = {};
      filteredStudents.forEach((student) => {
        attendanceData[student.enrollmentNo] = {
          enrollmentNo: student.enrollmentNo,
          name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim(),
          branch: student.branch,
          section: student.section,
          subject: timetableEntry?.subject,
          period: selectedPeriod,
          semester: timetableEntry?.semester,
          date: selectedDate,
          markedBy: router.state?.loginid,
          markedByName: facultyData ? `${facultyData.firstName} ${facultyData.lastName}` : "",
          isSubstitute: attendanceMode === "substitute",
          originalFaculty: attendanceMode === "substitute" ? selectedLeave?.facultyId : null,
          originalFacultyName: attendanceMode === "substitute" ? selectedLeave?.facultyName : null
        };
      });
      setMarkedAttendance(attendanceData);
    } else {
      setMarkedAttendance({});
    }
  };

  // Handle presentees input
  const handlePresenteesInput = (input) => {
    setPresenteesInput(input);
    
    if (!input.trim()) return;
    
    const presentees = input.split(',').map(e => e.trim()).filter(e => e);
    
    const newAttendance = {};
    filteredStudents.forEach((student) => {
      if (presentees.includes(student.enrollmentNo)) {
        newAttendance[student.enrollmentNo] = {
          enrollmentNo: student.enrollmentNo,
          name: `${student.firstName} ${student.middleName ? student.middleName + ' ' : ''}${student.lastName}`.trim(),
          branch: student.branch,
          section: student.section,
          subject: timetableEntry?.subject,
          period: selectedPeriod,
          semester: timetableEntry?.semester,
          date: selectedDate,
          markedBy: router.state?.loginid,
          markedByName: facultyData ? `${facultyData.firstName} ${facultyData.lastName}` : "",
          isSubstitute: attendanceMode === "substitute",
          originalFaculty: attendanceMode === "substitute" ? selectedLeave?.facultyId : null,
          originalFacultyName: attendanceMode === "substitute" ? selectedLeave?.facultyName : null
        };
      }
    });
    
    setMarkedAttendance(newAttendance);
    setSelectAllChecked(presentees.length === filteredStudents.length);
  };

  // Handle absentees input
  const handleAbsenteesInput = (input) => {
    setAbsenteesInput(input);
    
    if (!input.trim()) return;
    
    const absentees = input.split(',').map(e => e.trim()).filter(e => e);
    
    const newAttendance = { ...markedAttendance };
    absentees.forEach(enrollment => {
      delete newAttendance[enrollment];
    });
    
    setMarkedAttendance(newAttendance);
    setSelectAllChecked(Object.keys(newAttendance).length === filteredStudents.length);
  };

  const submitAttendance = async () => {
    if (Object.keys(markedAttendance).length === 0) {
      toast.error("No students selected for attendance");
      return;
    }

    if (!selectedDate || !selectedPeriod || !timetableEntry) {
      toast.error("Please select date and period");
      return;
    }

    setSubmitting(true);
    toast.loading("Submitting attendance...");

    try {
      const attendanceArray = Object.values(markedAttendance);
      console.log("Submitting attendance:", attendanceArray);
      
      const response = await axios.post(
        `${baseApiURL()}/attendence/addBulk`,
        attendanceArray
      );

      toast.dismiss();
      setSubmitting(false);

      if (response.data.success) {
        toast.success("Attendance submitted successfully!");
        setMarkedAttendance({});
        setSelectAllChecked(false);
        setAbsenteesInput("");
        setPresenteesInput("");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.dismiss();
      setSubmitting(false);
      console.error("Error submitting attendance:", error);
      toast.error(error.response?.data?.message || "Failed to submit attendance");
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex justify-between items-center pb-4 border-b border-slate-200 dark:border-slate-800">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">Substitute Attendance</h2>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-xs font-semibold text-slate-500 dark:text-slate-400">Loading substitute duties...</p>
        </div>
      )}

      {/* Assigned Leaves */}
      {assignedLeaves.length > 0 && !selectedLeave && (
        <div className="space-y-4">
          <h3 className="font-bold text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300">Faculty on Leave - You are the Substitute</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {assignedLeaves.map((leave) => (
              <div
                key={leave._id}
                className="border border-slate-200 dark:border-slate-800 rounded-2xl p-4 hover:shadow-xs cursor-pointer transition-all bg-indigo-50/40 dark:bg-indigo-950/30 hover:border-indigo-500"
                onClick={() => handleLeaveSelect(leave)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-sm text-slate-900 dark:text-white">{leave.facultyName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Employee ID: <span className="font-mono font-bold text-indigo-600 dark:text-indigo-400">{leave.facultyId}</span></p>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-400 mt-2">Leave Dates:</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {leave.dates.map((date, idx) => (
                        <span key={idx} className="text-[10px] font-bold bg-white dark:bg-slate-900 px-2 py-0.5 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">
                          {new Date(date).toLocaleDateString()}
                        </span>
                      ))}
                    </div>
                    <p className="text-[11px] font-bold text-emerald-600 dark:text-emerald-400 mt-2">Status: {leave.status}</p>
                  </div>
                  <button className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-bold transition shadow-xs">
                    Take Attendance
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No leaves assigned */}
      {!loading && assignedLeaves.length === 0 && !selectedLeave && (
        <div className="text-center py-10 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl">
          <svg className="w-12 h-12 text-slate-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">No substitute duties assigned at the moment.</p>
        </div>
      )}

      {/* Attendance Mode Selection */}
      {selectedLeave && (
        <div className="space-y-4">
          <div className="bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/80 rounded-2xl p-4">
            <p className="text-xs text-amber-900 dark:text-amber-300">
              You are taking attendance for <strong className="text-slate-900 dark:text-white">{selectedLeave.facultyName}</strong> who is on leave.
            </p>
          </div>

          <div className="flex space-x-2">
            <button
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                attendanceMode === "self"
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
              onClick={() => {
                setAttendanceMode("self");
                setTimetableEntry(null);
                setFilteredStudents([]);
                setMarkedAttendance({});
              }}
            >
              My Classes
            </button>
            <button
              className={`px-4 py-2 rounded-xl text-xs font-bold transition cursor-pointer ${
                attendanceMode === "substitute"
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
              onClick={() => {
                setAttendanceMode("substitute");
                setTimetableEntry(null);
                setFilteredStudents([]);
                setMarkedAttendance({});
              }}
            >
              {selectedLeave.facultyName}'s Classes
            </button>
          </div>

          {/* Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block font-bold text-xs text-slate-700 dark:text-slate-300 mb-1.5">Select Date</label>
              <select
                value={selectedDate}
                onChange={(e) => handleDateSelect(e.target.value)}
                className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">-- Select Date --</option>
                {selectedLeave.dates.map((date) => (
                  <option key={date} value={date}>
                    {new Date(date).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-bold text-xs text-slate-700 dark:text-slate-300 mb-1.5">Period</label>
              <select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                disabled={!selectedDate}
                className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                <option value="">-- Select Period --</option>
                {[...Array(9).keys()].map((i) => (
                  <option key={i + 1} value={i + 1}>
                    Period {i + 1}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-bold text-xs text-slate-700 dark:text-slate-300 mb-1.5">Day</label>
              <input
                type="text"
                value={selectedDay}
                disabled
                className="w-full px-3.5 py-2 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-100 dark:bg-slate-950 text-slate-500 dark:text-slate-400 text-xs font-semibold"
              />
            </div>
          </div>

          {/* Class Info */}
          {timetableEntry && (
            <div className="bg-indigo-50/40 dark:bg-slate-950 p-4 rounded-2xl border border-indigo-100 dark:border-slate-800">
              <h3 className="font-bold text-xs text-slate-900 dark:text-white mb-2 uppercase tracking-wider">Class Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-slate-400 block font-medium">Subject:</span>
                  <p className="font-bold text-slate-800 dark:text-slate-200">{timetableEntry.subject}</p>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">Branch:</span>
                  <p className="font-bold text-slate-800 dark:text-slate-200">{timetableEntry.branch}</p>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">Semester:</span>
                  <p className="font-bold text-slate-800 dark:text-slate-200">{timetableEntry.semester}</p>
                </div>
                <div>
                  <span className="text-slate-400 block font-medium">Section:</span>
                  <p className="font-bold text-slate-800 dark:text-slate-200">{timetableEntry.section}</p>
                </div>
              </div>
            </div>
          )}

          {/* Bulk Input Fields */}
          {filteredStudents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block font-bold text-xs text-slate-700 dark:text-slate-300 mb-1.5">
                  Presentees (comma-separated enrollment numbers)
                </label>
                <textarea
                  value={presenteesInput}
                  onChange={(e) => handlePresenteesInput(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  rows="2"
                  placeholder="Enter enrollment numbers separated by commas"
                />
              </div>
              <div>
                <label className="block font-bold text-xs text-slate-700 dark:text-slate-300 mb-1.5">
                  Absentees (comma-separated enrollment numbers)
                </label>
                <textarea
                  value={absenteesInput}
                  onChange={(e) => handleAbsenteesInput(e.target.value)}
                  className="w-full px-3.5 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                  rows="2"
                  placeholder="Enter enrollment numbers separated by commas"
                />
              </div>
            </div>
          )}

          {/* Student List */}
          {filteredStudents.length > 0 && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                  Students ({filteredStudents.length} found)
                </h3>
                <label className="flex items-center space-x-2 text-xs font-bold text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectAllChecked}
                    onChange={toggleSelectAll}
                    className="rounded accent-indigo-600"
                  />
                  <span>Select All</span>
                </label>
              </div>

              <div className="overflow-x-auto max-h-96 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-2xl">
                <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                  <thead className="sticky top-0 bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                    <tr>
                      <th className="py-2.5 px-4 text-center">Select</th>
                      <th className="py-2.5 px-4 text-left">Enrollment No</th>
                      <th className="py-2.5 px-4 text-left">Name</th>
                      <th className="py-2.5 px-4 text-left">Branch</th>
                      <th className="py-2.5 px-4 text-left">Semester</th>
                      <th className="py-2.5 px-4 text-left">Section</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                    {filteredStudents.map((student) => (
                      <tr key={student.enrollmentNo} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="py-2.5 px-4 text-center">
                          <input
                            type="checkbox"
                            checked={!!markedAttendance[student.enrollmentNo]}
                            onChange={() => toggleAttendance(student)}
                            className="rounded accent-indigo-600"
                          />
                        </td>
                        <td className="py-2.5 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">{student.enrollmentNo}</td>
                        <td className="py-2.5 px-4 font-semibold text-slate-900 dark:text-white">
                          {student.firstName} {student.middleName} {student.lastName}
                        </td>
                        <td className="py-2.5 px-4">{student.branch}</td>
                        <td className="py-2.5 px-4">{student.semester}</td>
                        <td className="py-2.5 px-4">{student.section}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Submit Button */}
              <div className="pt-2 text-center">
                <button
                  onClick={submitAttendance}
                  disabled={submitting || Object.keys(markedAttendance).length === 0}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold text-xs transition shadow-xs disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {submitting ? "Submitting..." : "Submit Attendance"}
                </button>
                <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-1.5">
                  {Object.keys(markedAttendance).length} students selected for attendance
                </p>
              </div>
            </div>
          )}

          {/* No students message */}
          {timetableEntry && filteredStudents.length === 0 && (
            <div className="text-center py-8 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">No students found for this class.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubstituteAttendance;